import type { AuthUser } from '@attendance/shared';
import { useAuthStore } from '../../stores/authStore.js';
import { heldPermissions } from '../../hooks/usePermission.js';

/* ===========================================================================
 * WHERE A NOTIFICATION TAKES SOMEBODY WHO RUNS THE THING IT IS ABOUT.
 *
 * The server stores one link per notification, written for the person it is
 * about: "your leave was approved" → `/leave`, "a task was given to you" →
 * `/tasks?task=…`. Those are personal paths, and the shell decides workspace
 * by path (`shellSurface.ts`), so an admin who opened one from the bell was
 * dropped out of the console into "My workspace" — onto a list the console
 * already shows them, from the side they manage it.
 *
 * So the link is read against who is opening it. Someone who manages that
 * area goes to its admin door, with the same query (a task id still opens the
 * task); everybody else goes exactly where the server said. Permission, not the
 * current workspace, decides: `/notifications` is itself a personal path, so
 * the workspace there always reads "personal".
 * ======================================================================== */

type Who = Pick<AuthUser, 'role' | 'permissions'> | null | undefined;

interface Door {
  /** The personal path the server writes. Matched exactly, query aside. */
  from: string;
  to: string;
  manages: (user: NonNullable<Who>, held: ReadonlySet<string>) => boolean;
}

const DOORS: readonly Door[] = [
  // The board shows an assignee's actions too (`TaskDetailPanel` isAssignee).
  { from: '/tasks', to: '/tasks/manage', manages: (_u, h) => h.has('tasks.view') },
  /* NO DOOR FOR `/corrections` OR `/leave`. The server writes those only to
     tell a requester their OWN request was decided, and since nobody decides
     their own (2026-10-15 audit, M-25) an admin's own correction or leave
     waits on "My requests" like anyone's — it is not on the approval queue
     they would have been sent to. */
  {
    from: '/announcements',
    to: '/announcements/manage',
    manages: (_u, h) => h.has('announcements.view'),
  },
  { from: '/letters', to: '/letters/approvals', manages: (_u, h) => h.has('letters.approve') },
];

/** The link to follow, for this user. `null` stays `null`. */
export function resolveNotificationLink(link: string | null, user: Who): string | null {
  if (!link || !user) return link;
  const q = link.indexOf('?');
  const path = q < 0 ? link : link.slice(0, q);
  const query = q < 0 ? '' : link.slice(q);
  const door = DOORS.find((d) => d.from === path);
  if (!door) return link;
  const held = new Set(heldPermissions(user) ?? []);
  return door.manages(user, held) ? `${door.to}${query}` : link;
}

/** `resolveNotificationLink` for the signed-in user. */
export function useNotificationLink(link: string | null): string | null {
  const user = useAuthStore((s) => s.user);
  return resolveNotificationLink(link, user);
}
